/**
 * The fifth live route (#30 / R7.2) — the family's shared village goal, on real data.
 *
 * **What the server actually has.** `GET /api/village` returns exactly this, and nothing else
 * (`village.service.js:43-51`):
 *
 *     { name, pointTarget, startedAt, earnedPoints, projectProgress, unlockedFractions }
 *
 * A name, a target, a start, a count. `village: null` means nobody has started a project — the
 * route says so in its own docblock — and it does NOT mean an empty village.
 *
 * **What the SCREEN would say if you just handed it that.** `VillageProjectCard` carries four
 * defaults that read as facts about a real household and are not:
 *
 * | default | what the server has |
 * | --- | --- |
 * | `eyebrow = 'Village project · this week'` | `village_projects` has `started_at` and no end. There is no week. |
 * | `unit = 'lanterns'` | the view counts approval POINTS (`village_stats.earned_points`). A lantern is not a point. |
 * | `rewardName = 'Festival Stage'` + `rewardOutcome` | no endpoint carries a reward at all |
 * | `emptyLabel = '… a new one begins on Monday.'` | nothing schedules a Monday |
 *
 * This is the same defect family `/approvals` shipped as **"3 of 0"** and **"Payday — Week null"**:
 * a confident sentence about a real family, stated by a default nobody chose. So this container
 * overrides every one of them with a true value, or with the `null` the card documents as "hide
 * this". It does not pass `undefined` and hope — `undefined` re-selects the default.
 *
 * **#583/#584 N1 S8 adds two more reads, each on its own.** `GET /api/village/story` (S5) carries the
 * permanent milestones, the houses and the contributors in name order. While its switch is off it
 * answers `{ enabled: false }`, and a server older than S5 answers 404; both leave the project card
 * on the compatibility read from `GET /api/village`. When it is on, the card counts the story total, so the
 * screen never shows two totals for one project. `GET /api/village/presence` (E26) says who has the
 * app open, with its own timestamp, whatever the switch says. It is re-read on focus, every minute
 * while the tab is visible, and on the screen's retry, and only the newest read lands. The route owns
 * the ONE polite sentence about who arrived; the feed announces nothing itself. The story's dates are
 * in the household's zone, from `GET /api/tenants/current` (review r1).
 *
 * **Naming the goal lives on the Family screen** (S4), not here, and the notice says so.
 *
 * **The future-world signpost** (#583 lines 53 and 63, #601 line 71) says only what the one versioned
 * claims source says: a companion game is planned and none has been released
 * (`shared/legal-source.json`, `availability.companionMmoWording`, the homepage's sentence). No other
 * families, race, ranking, multiplayer or date. `toSignpost` hides it once that source says anything else.
 *
 * **The vanishing-warning trap**, the same one `OnboardingRoute` and `ApprovalsRoute` document:
 * going live leaves fixture mode, which silently REMOVES `SnapshotNotice`. This route carries its
 * own note instead.
 *
 * **A family turn (#1003)** opens two of these reads to the child, the goal and the story, each naming the turn's
 * generation, which the server requires. It keeps who is connected, `/api/village-stats` and the household settings
 * closed, so during a turn the screen never asks for them: no presence panel, and the story's dates are in UTC and
 * say so. One sentence says what the turn does not show (`turnReadMessage`). A refused goal says the Village is not
 * available during this turn, with no retry and no re-read: waiting does not open it. A reload mounts this screen
 * before the session answers, so nothing is read until it says whether a turn is on.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import legal from '~shared/legal-source.json';

import LanternLoader from '../components/LanternLoader.jsx';
import VillageStory, { makeDateFormatter } from '../components/VillageStory.jsx';
import { fetchTenant } from '../api/session.js';
import { ApiError, api } from '../api/client.js';
import { reportTurnRefusal, turnReadMessage, turnReadStatus } from '../lib/familyInteraction.js';
import { turnSubject } from '../lib/familyTurnSubject.js';
import { toVillageProject } from '../lib/villageMapping.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import { freshnessLead, useLiveRefresh } from '../state/HouseholdRealtime.jsx';
import { arrivalSummary, toPresence, toStoryProjectCard, toVillageStory } from '../lib/villageStoryMapping.js';

const Village = lazy(() => import('../pages/Village.jsx'));

/**
 * S9's 3D village joins the story through its seam (#583/#584 N1 S9). The panel is its own lazy chunk,
 * because this route is in the app's entry chunk and every route would pay for it (review H3, measured:
 * +2,080 B gz on the entry). It holds no `three`: the library and the model load only after the family
 * presses "Explore the scene". Until the panel arrives the story and the poster are already there.
 */
function SceneUnavailable() {
  return <p role="status">The 3D village could not load. The painting and the story list are still available.</p>;
}
const VillageScene = lazy(() => import('../components/VillageScene.jsx').catch(() => ({ default: SceneUnavailable })));
const renderScene = (seam) => <Suspense fallback={null}><VillageScene {...seam} /></Suspense>;

/** Presence is who has the app open NOW, so it is re-read; a minute is plenty for a calm screen. */
export const PRESENCE_REFRESH_MS = 60_000;

/**
 * Carried on the page, because leaving fixture mode removes the fixture banner. Story switch off.
 * Both notices open with `freshnessLead(status)`: "Live family data." only while live (#580).
 */
export const NOT_BUILT_NOTICE =
  'The milestones your village keeps are not switched on yet, so they are not '
  + 'shown. A parent names the household goal on the Family screen.';

/** The same page with the story switch on. */
export const STORY_NOTICE = 'A parent names the household goal on the Family screen.';

/** Said beside the notice when the story read fails for a reason other than "not here yet". */
export const STORY_FAILED = 'The village’s milestones could not be loaded just now.';

/**
 * Said beside the notice while the story read is still in flight. It makes no claim about the switch:
 * "not switched on" is said only for a real off answer (review r1 H1).
 */
export const STORY_LOADING = 'The village’s milestones are still loading.';

/**
 * Said while the read is still in flight or has failed.
 *
 * The ready note opens with "Live family data." while live, which is a claim, and it is false at the
 * exact moment no data has arrived. Two sentences, each true in its own state.
 */
export const NOT_LIVE_YET = 'Your village has not loaded yet, so nothing here describes your family.';

/** Under the poster: what the picture is, and what it is not (D1: no invented family statistics). */
export const POSTER_CAPTION =
  'A painting of a lantern village at dusk. It is not a map of your family’s village: your family’s '
  + 'recorded milestones are shown only when they can be loaded.';

export const POSTER_CANDIDATES = [
  { src: '/art/village-hero-480.webp', width: 480 },
  { src: '/art/village-hero-960.webp', width: 960 },
  { src: '/art/village-hero.jpg', width: 1376 },
];

/**
 * The signpost's words, or `null` when the claims source no longer records the companion game as not
 * released: the sentence then stops being true, and the page shows no signpost rather than a stale one.
 * All three slots are set, because an omitted slot re-selects `RoadmapSignpost`'s mockup default about
 * "other families' lights".
 */
export function toSignpost(availability) {
  const wording = availability?.companionMmoWording;
  if (availability?.companionMmoStatus !== 'not-released' || typeof wording !== 'string' || !wording.trim()) return null;
  // d-20261001-39: no timing promise. The game's own roadmap is at M0, so the signpost names it and says it is planned.
  return { title: 'Companion game', description: wording, chipLabel: 'Planned', ariaLabel: 'Companion game' };
}
const SIGNPOST = toSignpost(legal.availability);

export const PRESENCE_TITLE = 'Connections last checked';
export const PRESENCE_NOBODY = 'No connections were seen in this check.';
export const PRESENCE_UNCHECKED = 'Who is in the village right now could not be checked.';

/** The empty page, said without the mechanism this screen does not have. */
export const EMPTY_TITLE = 'No village project yet ✦';
export const EMPTY_BODY =
  'A village project is a goal your family works towards together, and the points from every '
  + 'approved quest count towards it. Nobody has started one yet.';
export const EMPTY_SUBTITLE = 'Your village is waiting for its first goal ✦';

/**
 * The scene's own subtitle, replacing a default this container cannot stand behind.
 *
 * `Village.jsx` defaults it to "Every lantern your family lights, the village keeps. It only ever
 * grows — nothing here can be lost." That describes a mechanism — a village that keeps and shows
 * every lantern — and nothing on this screen delivers it: the buildings panel has no endpoint and
 * is not rendered. Found by the gpt-6-astra repair verifier, which correctly noted it predates
 * this wiring and equally correctly refused to let it stand on a live screen.
 */
export const SCENE_SUBTITLE =
  'Shared progress and recorded milestones for your family’s village.';

/** `village: null` is an answer: nobody has started one. It is not an empty village. */
export const NO_PROJECT_YET =
  'There is no current village project. A parent starts one on the Family screen.';

export const LOAD_FAILED =
  "We couldn't load your village just now — give it another moment.";

/**
 * One sentence for a failed read.
 *
 * Unlike `/approvals`, this endpoint is NOT parent-only: `village.routes.js:13` applies
 * `requireAuth` alone, because the village is the family's shared goal and every member sees it.
 * So a retry here is a door that can actually open, and offering one is right.
 *
 * #1003: a family turn's refusal is not such a door. It says the turn's sentence (`turnReadMessage`), never "give it
 * another moment", and the screen offers no retry for it (`isTurnRefusal`).
 */
export function loadMessage(error) {
  if (isTurnRefusal(error)) return turnReadMessage('village', turnReadStatus(error));
  if (error instanceof ApiError && error.status === 401) {
    return 'Your sign-in has expired — please sign in again.';
  }
  return LOAD_FAILED;
}

/**
 * The story state after a story read, from the one place both reads use. 'off' covers
 * `{ enabled: false }` AND a 404 from a server older than S5, which must read the same. Anything
 * else that fails keeps the last confirmed story, which the note then labels as possibly stale.
 */
const storyAnswered = (data) => (prev) => {
  const view = toVillageStory(data);
  if (view && view.enabled) return { status: 'on', view };
  return { status: view ? 'off' : 'failed', view: view ? null : prev.view };
};
const storyFailed = (error) => (prev) => ({
  status: error instanceof ApiError && error.status === 404 ? 'off' : 'failed',
  view: error instanceof ApiError && [401, 403, 404].includes(error.status) ? null : prev.view,
});

/** #1003: the server's (or the API client's) refusal of a family turn's read, which asking again does not open. */
const isTurnRefusal = (error) => turnReadStatus(error) !== 'failed';

/** #1003: the dates a family turn shows. The household's zone is a closed read, so they are UTC and say so. */
const TURN_ZONE = Object.freeze({ status: 'done', timeZone: null });

/** #1003: what a turn holding no live member says: it has ended, its session has, or its state could not be read. */
const TURN_STATE_STATUS = Object.freeze({ locked: 'locked', revoked: 'revoked' });

/** #1003: the turn's member as this screen reads for it: only the generation, which both reads name. */
const villageMember = (member, familyTurn) => ({ generation: familyTurn.generation });

/** `GET /api/village`'s documented shape: `village` is an object, or `null` for "no project yet". */
const villageReadable = (data) => !!data && typeof data === 'object'
  && (data.village === null || (typeof data.village === 'object' && !Array.isArray(data.village)));

/**
 * The live `/village` route: a family turn's view (#1003), or the signed-in account's. Until the session's first
 * answer says which, nothing is read. Each turn (member and generation) gets its own screen, so nothing read for one
 * is drawn for the next.
 */
export default function VillageRoute() {
  const actorContext = useOptionalActor();
  const familyTurn = actorContext?.familyTurn ?? null;
  if (!familyTurn && actorContext?.status === 'loading') return <VillageScreen key="waiting" turn={null} waiting />;
  if (!familyTurn) return <VillageScreen key="account" turn={null} />;
  const subject = turnSubject(familyTurn, villageMember);
  const turn = { generation: subject.member?.generation ?? null, state: familyTurn.state };
  return <VillageScreen key={`turn:${subject.key}`} turn={turn} />;
}

/**
 * @param {object} props
 * @param {{generation: string|null, state: string}|null} props.turn  a family turn, or `null` for the account
 * @param {boolean} [props.waiting]  the session has not answered yet: read nothing
 */
function VillageScreen({ turn, waiting = false }) {
  const inTurn = !!turn;
  const generation = turn?.generation ?? null;
  // #1003: a turn with no live member (locked, revoked, unreadable) reads nothing, and says so.
  const memberless = inTurn && !generation;
  const reads = !waiting && !memberless;
  /** The goal or the story, naming the turn's generation when there is one; a turn's refusal reports the turn ended. */
  const villageGet = useCallback((path, signal) => (
    api.get(path, generation ? { signal, familyGeneration: generation } : { signal }).catch((error) => {
      if (!signal.aborted) reportTurnRefusal(generation, error);
      throw error;
    })
  ), [generation]);
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!reads) return undefined;
    const controller = new AbortController();
    let live = true;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    villageGet('/api/village', controller.signal)
      .then((data) => { if (live) setState({ status: 'ready', data, error: null }); })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', data: null, error });
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey, reads, villageGet]);

  // The story (`storyAnswered` / `storyFailed`). A failure is said aloud, and the rest of the screen stays.
  const [story, setStory] = useState({ status: 'loading', view: null });
  useEffect(() => {
    if (!reads) return undefined;
    const controller = new AbortController();
    let live = true;
    // Keep the last confirmed snapshot during a refresh, labelled stale until it succeeds.
    setStory((prev) => ({ ...prev, status: 'loading' }));
    villageGet('/api/village/story', controller.signal)
      .then((data) => { if (live) setStory(storyAnswered(data)); })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setStory(storyFailed(error));
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey, reads, villageGet]);

  // #580: both reads again when the household changes — the story too, because with its switch on
  // the project card counts the STORY total (review q1 H1). A failure keeps what is on screen: the
  // story lands on its own terms, and an unreadable village answer changes nothing. A turn's refusal stops
  // these re-reads (`useLiveRefresh` stops on a 403).
  const live = useLiveRefresh('village', {
    read: (signal) => Promise.all([
      villageGet('/api/village', signal).then((value) => ({ value }), (error) => ({ error })),
      villageGet('/api/village/story', signal).then(storyAnswered, storyFailed),
    ]),
    apply: ([result, nextStory]) => {
      setStory(nextStory);
      if (result.error) throw result.error;
      const data = result.value;
      if (!villageReadable(data)) return false;
      setState({ status: 'ready', data, error: null });
      return true;
    },
    enabled: state.status === 'ready',
  });

  const [presence, setPresence] = useState(null);
  // Both reads carry dates. Fetch the existing tenant resource only when there is a date to show;
  // keep the confirmed household zone across refreshes (timezone editing is not supported). A family
  // turn may not read it (#1003): its dates are UTC, labelled so.
  const [zone, setZone] = useState(() => (inTurn ? TURN_ZONE : { status: 'loading', timeZone: null }));
  const needsZone = !!story.view || !!presence?.asOf;
  useEffect(() => {
    if (inTurn || !needsZone || zone.timeZone) return undefined;
    let live = true;
    setZone({ status: 'loading', timeZone: null });
    fetchTenant()
      .then((tenant) => {
        if (live) setZone({ status: 'done', timeZone: typeof tenant?.timezone === 'string' ? tenant.timezone : null });
      })
      .catch(() => { if (live) setZone({ status: 'done', timeZone: null }); });
    return () => { live = false; };
  }, [inTurn, needsZone, reloadKey, zone.timeZone]);

  // Presence reads may overlap; only the newest response lands. The timestamp labels the snapshot,
  // including while a refresh is in flight. One summary is quiet in the modal's inert background.
  const [liveSummary, setLiveSummary] = useState('');
  const seenRef = useRef(null);
  const presenceSeqRef = useRef(0);
  useEffect(() => {
    // #1003: who is connected is closed to a family turn. No read, so no panel (`presence` stays `null`).
    if (waiting || inTurn) return undefined;
    let live = true;
    const read = () => {
      const seq = ++presenceSeqRef.current;
      const newest = () => live && seq === presenceSeqRef.current;
      return api.get('/api/village/presence')
        .then((data) => {
          if (!newest()) return;
          const next = toPresence(data);
          if (next.reachable) {
            setLiveSummary(arrivalSummary(seenRef.current, next.rows));
            seenRef.current = new Set(next.rows.map((r) => r.id));
          }
          setPresence(next);
        })
        .catch(() => { if (newest()) setPresence({ reachable: false, asOf: null, rows: [] }); });
    };
    // A hidden tab has nobody to tell; its next focus reads again (review r1 L4).
    const tick = () => { if (document.visibilityState !== 'hidden') read(); };
    read();
    const timer = setInterval(tick, PRESENCE_REFRESH_MS);
    window.addEventListener('focus', read);
    return () => { live = false; clearInterval(timer); window.removeEventListener('focus', read); };
  }, [reloadKey, waiting, inTurn]);

  // A retry starts the polite region clean, so a summary heard before the failure is not heard again.
  const reload = useCallback(() => {
    setLiveSummary('');
    setReloadKey((n) => n + 1);
  }, []);

  // Each successful source stands on its own. An enabled story is authoritative even when it
  // names no current project; the compatibility read cannot resurrect a replaced project.
  const storyStatus = story.view && zone.status === 'loading' ? 'loading' : story.status;
  const storyView = zone.status === 'done' ? story.view : null;
  const legacyProject = useMemo(() => toVillageProject(state.data), [state.data]);
  const onStory = story.view;
  const project = onStory ? toStoryProjectCard(onStory.current) : legacyProject;
  const ready = !!onStory || !!state.data;
  const loadError = memberless ? turnReadMessage('village', TURN_STATE_STATUS[turn.state] ?? 'failed')
    : !ready && state.status === 'error' ? loadMessage(state.error) : null;
  // A refusal, or a turn that has ended, is not a door that opens by asking again.
  const retryable = !memberless && !(loadError && isTurnRefusal(state.error));
  const stale = !!story.view && story.status !== 'on';
  const notice = stale ? 'Showing the last confirmed story; it may be out of date.'
    : [freshnessLead(live.status), storyStatus === 'off' ? NOT_BUILT_NOTICE : STORY_NOTICE].filter(Boolean).join(' ');
  const parts = !ready ? [NOT_LIVE_YET] : project || onStory?.unrecorded ? [notice] : [NO_PROJECT_YET, notice];
  if (ready && storyStatus === 'loading') parts.push(STORY_LOADING);
  if (ready && storyStatus === 'failed') parts.push(STORY_FAILED);
  if (!onStory && state.data && state.status !== 'ready') parts.push('The project shown is from the last successful read.');
  // #1003: what a turn does not show. The story names its contributors, so the sentence depends on whether it shows.
  if (inTurn && ready && storyStatus !== 'loading') parts.push(turnReadMessage(storyView ? 'villagePartsStory' : 'villageParts', 'forbidden'));
  const note = parts.join(' ');
  const formatPresence = useMemo(() => makeDateFormatter(zone.timeZone, {
    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }), [zone.timeZone]);
  const presenceNote = presence?.reachable
    ? `${presence.scope === 'this-server-process' ? 'Connections seen by this server' : 'Connections returned by this check'} · As of ${formatPresence(presence.asOf)}.`
    : null;

  return (
    <Suspense fallback={<LanternLoader label="Loading your village…" />}>
      <Village
        showSnapshotNotice={false}
        project={project}
        note={note}
        // No `trophiesHref` yet: `/trophies` is the fixture gallery until S7 wires it (review r1 M4).
        // Pass `trophiesHref="#/trophies"` here in the change that adds it to LIVE_CONTAINERS.
        story={storyView ? <VillageStory story={storyView} timeZone={zone.timeZone} renderScene={renderScene} /> : null}
        sceneCaption={POSTER_CAPTION}
        scenePosterAlt="A painting of a lantern village at dusk."
        scenePosterCandidates={POSTER_CANDIDATES}
        onRefresh={ready && retryable ? reload : null}
        // `null` until the first presence read answers, so no panel speaks before it knows.
        presence={presence ? presence.rows : null}
        presenceTitle={PRESENCE_TITLE}
        presenceEmptyLabel={presence && !presence.reachable ? PRESENCE_UNCHECKED : PRESENCE_NOBODY}
        presenceNote={presenceNote}
        presenceFormatWhen={formatPresence}
        presenceLive={false}
        liveSummary={liveSummary}
        // `null`, NOT `[]`. An empty array says "I read it, and there was nothing", and the
        // legend then answers with its own warm copy: "your village is just getting started —
        // every quest your family finishes builds something here". That would tell a family at
        // 300 approvals they are just getting started. The houses and milestones live in the
        // story instead, from awards the server recorded.
        buildings={null}
        // The page's own empty copy promises "the first cottage rises". Nothing populates
        // buildings, so that is a mechanism, not a fact.
        emptyTitle={EMPTY_TITLE}
        emptyBody={EMPTY_BODY}
        emptySubtitle={EMPTY_SUBTITLE}
        roadmap={SIGNPOST}
        sceneSubtitle={SCENE_SUBTITLE}
        sceneChips={[]}
        sceneGlints={[]}
        loading={!ready && !memberless && state.status === 'loading'}
        error={loadError}
        onRetry={retryable ? reload : undefined}
      />
    </Suspense>
  );
}
